"use client";

import { useCallback, useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { useCopilotDrawerState as useOriginalCopilotDrawerState } from "@zapier/ai-toolkit/client";
import { isCopilotEnabledAtom_SUSPENDS } from "../canvasAi";
import { shouldCopilotBeVisibleAtom } from "../canvasAi";
import { adjustSidebarWidthOnCopilotSetter } from "../diagramming";
import { useIsCopilotNoPilotDisabled } from "./useIsCopilotNoPilotDisabled";

/**
 * Wrapper hook that combines copilot drawer state with enabled state.
 *
 * Returns [isOpen, setIsOpen] where:
 * - isOpen: true only if copilot is enabled AND drawer is open
 * - setIsOpen: function to control drawer state (with availability check)
 *
 * Sidebar width is adjusted automatically whenever isCopilotOpen changes.
 * This eliminates the need for components to manually check copilot availability
 * (isCopilotEnabled, shouldCopilotBeVisible, etc.) when working with copilot drawer state.
 *
 * Use this instead of the original useCopilotDrawerState from ai-toolkit/client
 */
export function useCopilotDrawerState(): [
  boolean,
  (value: boolean | ((prev: boolean) => boolean)) => void,
] {
  const [isOpen, setIsOpen] = useOriginalCopilotDrawerState();
  const isCopilotEnabled = useAtomValue(isCopilotEnabledAtom_SUSPENDS);
  const shouldCopilotBeVisible = useAtomValue(shouldCopilotBeVisibleAtom);
  const adjustSidebarWidth = useSetAtom(adjustSidebarWidthOnCopilotSetter);

  /*
   * Once useIsCopilotNoPilotDisabled is removed, we could convert to atoms:
   * - Sync useOriginalCopilotDrawerState() to an atom in CanvasHydrator
   * - Convert useCopilotDrawerState to use atoms instead of the hook
   * Only do this if we need copilot state in other atoms (atom-to-atom dependencies).
   * Otherwise, the hook approach is simpler and sufficient for component usage.
   */
  const isCopilotNoPilotDisabled = useIsCopilotNoPilotDisabled();
  const isCopilotAvailable =
    !isCopilotNoPilotDisabled && isCopilotEnabled && shouldCopilotBeVisible;
  const isCopilotOpen = isCopilotAvailable && isOpen;

  /*
   * Reactively adjust sidebar width whenever copilot open state changes.
   * This ensures width stays in sync regardless of how state changes
   * (manual toggle, initial load from localStorage, etc.).
   * Other sidebar states are handled by sideBarSelectedAtom.
   * See bug: https://cdn.zappy.app/7059c2912899e9de656897a2cc830b78.gif
   */
  useEffect(() => {
    adjustSidebarWidth(isCopilotOpen);
  }, [isCopilotOpen, adjustSidebarWidth]);

  /*
   * Prevents opening/closing copilot when it's disabled or not available.
   */
  const setIsCopilotOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      if (!isCopilotAvailable) return;
      setIsOpen(value);
    },
    [isCopilotAvailable, setIsOpen],
  );

  // Return the enabled-aware state and wrapped setter
  return [isCopilotOpen, setIsCopilotOpen];
}
